/**
 * Authentication utilities for JWT token management
 */

const TOKEN_KEY = 'spatium_access_token';
const REFRESH_TOKEN_KEY = 'spatium_refresh_token';
const USER_KEY = 'spatium_user';

// Global auth error callback
let globalAuthErrorCallback = null;

/**
 * Set global authentication error callback
 * @param {Function} callback - Callback function for auth errors
 */
export const setGlobalAuthErrorCallback = (callback) => {
  globalAuthErrorCallback = callback;
};

/**
 * Store authentication data in localStorage
 * @param {Object} authData - Auth data from login response
 */
export const storeAuthData = (authData) => {
  try {
    if (authData.tokens?.accessToken) {
      localStorage.setItem(TOKEN_KEY, authData.tokens.accessToken);
    }
    if (authData.tokens?.refreshToken) {
      localStorage.setItem(REFRESH_TOKEN_KEY, authData.tokens.refreshToken);
    }
    if (authData.user) {
      localStorage.setItem(USER_KEY, JSON.stringify(authData.user));
    }
  } catch (error) {
    console.error('Failed to store auth data:', error);
  }
};

/**
 * Get stored access token
 * @returns {string|null} Access token or null if not found
 */
export const getAccessToken = () => {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch (error) {
    console.error('Failed to get access token:', error);
    return null;
  }
};

/**
 * Get stored refresh token
 * @returns {string|null} Refresh token or null if not found
 */
export const getRefreshToken = () => {
  try {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  } catch (error) {
    console.error('Failed to get refresh token:', error);
    return null;
  }
};

/**
 * Get stored user data
 * @returns {Object|null} User data or null if not found
 */
export const getStoredUser = () => {
  try {
    const userData = localStorage.getItem(USER_KEY);
    return userData ? JSON.parse(userData) : null;
  } catch (error) {
    console.error('Failed to get stored user:', error);
    return null;
  }
};

/**
 * Check if user is authenticated (has valid token)
 * @returns {boolean} True if authenticated
 */
export const isAuthenticated = () => {
  const token = getAccessToken();
  if (!token) return false;
  
  // Check if token is expired
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Math.floor(Date.now() / 1000);
    return payload.exp > currentTime;
  } catch (error) {
    console.error('Failed to parse token:', error);
    return false;
  }
};

/**
 * Check if token is close to expiry (within 5 minutes)
 * @returns {boolean} True if token expires soon
 */
export const isTokenExpiringSoon = () => {
  const token = getAccessToken();
  if (!token) return true;
  
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Math.floor(Date.now() / 1000);
    const fiveMinutes = 5 * 60; // 5 minutes in seconds
    return payload.exp - currentTime < fiveMinutes;
  } catch (error) {
    console.error('Failed to check token expiry:', error);
    return true;
  }
};

/**
 * Get time until token expires
 * @returns {number} Seconds until expiry, or 0 if expired/invalid
 */
export const getTokenTimeToExpiry = () => {
  const token = getAccessToken();
  if (!token) return 0;
  
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Math.floor(Date.now() / 1000);
    return Math.max(0, payload.exp - currentTime);
  } catch (error) {
    console.error('Failed to get token expiry time:', error);
    return 0;
  }
};

/**
 * Clear all authentication data from localStorage
 */
export const clearAuthData = () => {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    
    // Also clear any legacy auth data
    localStorage.removeItem('user'); // Old key
  } catch (error) {
    console.error('Failed to clear auth data:', error);
  }
};

/**
 * Create authorization header for API requests
 * @returns {Object} Headers object with Authorization
 */
export const getAuthHeaders = () => {
  const token = getAccessToken();
  if (!token) {
    return {};
  }
  
  return {
    'Authorization': `Bearer ${token}`
  };
};

/**
 * Handle authentication errors from API responses
 * @param {Response} response - Fetch response object
 * @returns {boolean} True if auth error was handled
 */
export const handleAuthError = (response) => {
  // 401 = unauthenticated -> clear session and redirect to login
  if (response.status === 401) {
    console.warn('Authentication failed (401), clearing auth data');
    clearAuthData();
    if (globalAuthErrorCallback) {
      globalAuthErrorCallback();
    } else {
      setTimeout(() => {
        window.location.reload();
      }, 100);
    }
    return true;
  }

  // 403 = forbidden (insufficient permissions). Do NOT clear session.
  // Let callers handle this gracefully (e.g., hide data or show Access Denied UI).
  if (response.status === 403) {
    console.warn('Authorization denied (403). Keeping session; feature may be restricted.');
    return false;
  }

  return false;
};

/**
 * Check if current user has admin role
 * @returns {boolean} True if user is admin
 */
export const isAdmin = () => {
  const user = getStoredUser();
  return user?.role === 'admin';
};

/**
 * Check if current user is a read-only viewer (training role)
 * Viewers can view data but cannot create, update, or delete anything
 * @returns {boolean} True if user has the read-only viewer role
 */
export const isViewer = () => {
  const user = getStoredUser();
  return user?.role === 'viewer';
};

/**
 * Check if current user has specific role
 * @param {string} role - Role to check
 * @returns {boolean} True if user has the role
 */
export const hasRole = (role) => {
  const user = getStoredUser();
  return user?.role === role;
};

/**
 * Get current user's role
 * @returns {string|null} User role or null
 */
export const getUserRole = () => {
  const user = getStoredUser();
  return user?.role || null;
};

/**
 * Migrate legacy localStorage user data to new JWT format
 * This helps with transition from old auth system
 */
export const migrateLegacyAuth = () => {
  try {
    const legacyUser = localStorage.getItem('user');
    if (legacyUser && !getStoredUser()) {
      // Legacy data exists but no new JWT data
      console.log('Migrating legacy auth data...');
      clearAuthData(); // Clear everything and force re-login
      return true;
    }
  } catch (error) {
    console.error('Failed to migrate legacy auth:', error);
  }
  return false;
};

/**
 * Refresh user data from server
 * @returns {Promise<Object|null>} Updated user data or null
 */
export const refreshUserData = async () => {
  try {
    const token = getAccessToken();
    if (!token) return null;

    // Match API base selection logic used in src/utils/api.js
    const API_URL_BASE = process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:5050/api';
    const response = await fetch(`${API_URL_BASE}/users/me`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (response.ok) {
      const userData = await response.json();
      // Normalize to always expose `id` (backend /me returns `_id`)
      const normalizedUser = {
        ...userData,
        id: userData.id || userData._id
      };
      // Update stored user data
      localStorage.setItem(USER_KEY, JSON.stringify(normalizedUser));
      return normalizedUser;
    } else {
      console.warn('Failed to refresh user data:', response.status);
      return getStoredUser();
    }
  } catch (error) {
    console.error('Error refreshing user data:', error);
    return getStoredUser();
  }
};

/**
 * Initialize authentication on app startup
 * @returns {Object|null} User data if authenticated, null otherwise
 */
export const initializeAuth = () => {
  // Check for legacy data migration
  migrateLegacyAuth();
  
  // Check if user is authenticated
  if (isAuthenticated()) {
    const userData = getStoredUser();
    
    // If user data doesn't have moduleAccess, we'll need to refresh it
    if (userData && !userData.moduleAccess) {
      console.log('User data missing moduleAccess, will refresh on next load');
    }
    
    return userData;
  } else {
    // Clear any invalid auth data
    clearAuthData();
    return null;
  }
};

/**
 * Force refresh user data and update localStorage
 * @returns {Promise<Object|null>} Updated user data or null
 */
export const forceRefreshUserData = async () => {
  try {
    const userData = await refreshUserData();
    if (userData) {
      return userData;
    } else {
      clearAuthData();
      return null;
    }
  } catch (error) {
    console.error('Force refresh error:', error);
    clearAuthData();
    return null;
  }
}; 